/**
 * Main entry point — Bootstrap React app with providers, router, and PWA service worker.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { AuthProvider } from '@/features/auth/auth.context';
import { ToastProvider } from '@/components/ui/Toast';
import './index.css';

// ============================================================
// React Query client
// ============================================================
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// ============================================================
// PWA service worker registration
// ============================================================
import { registerSW } from 'virtual:pwa-register';

// ────────────────────────────────────────────────────────────
// Development: Unregister stale service workers.
//
// In development, the Service Worker can cause issues with API
// requests (especially POST to /auth/callback) because the
// runtimeCaching rules may intercept non-GET requests. Even after
// fixing vite.config.ts, a previously-registered SW with the old
// caching rules will continue to run until it's updated.
//
// To avoid this, in development we:
//   1. Unregister any existing service workers on page load
//   2. Re-register the new SW (which has the corrected rules)
//
// In production, the SW is registered normally with autoUpdate.
// ────────────────────────────────────────────────────────────
const isDev = import.meta.env.DEV;

if ('serviceWorker' in navigator) {
  if (isDev) {
    // Unregister all existing service workers in dev to clear stale caches
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => {
        if (registrations.length > 0) {
          console.info(
            `[PWA] Dev mode: unregistering ${registrations.length} stale service worker(s)`,
          );
          return Promise.all(registrations.map((r) => r.unregister()));
        }
        return [];
      })
      .then((results) => {
        const unregistered = results.filter(Boolean).length;
        if (unregistered > 0) {
          console.info(`[PWA] Unregistered ${unregistered} service worker(s). Reloading...`);
          // Reload the page to ensure the new SW takes effect immediately
          window.location.reload();
          return;
        }
        // Register the fresh SW (with corrected caching rules)
        registerSW({
          onNeedRefresh() {
            console.info('[PWA] New content available, please refresh.');
          },
          onOfflineReady() {
            console.info('[PWA] App ready to work offline.');
          },
        });
      })
      .catch((err) => {
        console.warn('[PWA] Failed to manage service worker:', err);
      });
  } else {
    // Production: normal registration
    registerSW({
      onNeedRefresh() {
        console.info('[PWA] New content available, please refresh.');
      },
      onOfflineReady() {
        console.info('[PWA] App ready to work offline.');
      },
    });
  }
}

// ============================================================
// Render
// ============================================================
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ToastProvider>
            <App />
          </ToastProvider>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>,
);